import React, { useState, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  IconSearch,
  IconFilter,
  IconPlus,
  IconEye,
  IconEdit,
  IconTrash,
  IconQrcode,
  IconPrinter,
  IconArrowsExchange,
  IconAlertTriangle,
  IconFileText,
  IconHistory,
  IconBox,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import { useStoreSettings } from "../../controllers/settings.controller";
import { deleteAsset } from "../../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetRegisterTab({
  assets,
  categories,
  suppliers,
  onRefresh,
  onOpenAddAsset,
  onOpenEditAsset,
  onOpenTransfer,
  onOpenDamage,
  onViewAssetDetails,
}) {
  const { data: storeSettings } = useStoreSettings();
  const currency = storeSettings?.currency || "Rs.";

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedCondition, setSelectedCondition] = useState("all");
  const [qrAsset, setQrAsset] = useState(null);

  const filteredAssets = useMemo(() => {
    if (!Array.isArray(assets)) return [];
    return assets.filter((item) => {
      if (selectedCategory !== "all" && String(item.category_id) !== String(selectedCategory)) return false;
      if (selectedStatus !== "all" && item.status !== selectedStatus) return false;
      if (selectedCondition !== "all" && item.condition !== selectedCondition) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const matchName = (item.name || "").toLowerCase().includes(q);
        const matchTag = (item.asset_id_str || "").toLowerCase().includes(q);
        const matchSerial = (item.serial_number || "").toLowerCase().includes(q);
        const matchBarcode = (item.barcode || "").toLowerCase().includes(q);
        const matchBrand = (item.brand || "").toLowerCase().includes(q);
        const matchLoc = (item.location || "").toLowerCase().includes(q);
        const matchCustodian = (item.custodian_name || item.assigned_to || "").toLowerCase().includes(q);
        if (!matchName && !matchTag && !matchSerial && !matchBarcode && !matchBrand && !matchLoc && !matchCustodian) {
          return false;
        }
      }
      return true;
    });
  }, [assets, selectedCategory, selectedStatus, selectedCondition, search]);

  const handleDelete = async (id, name, tag) => {
    if (!confirm(`Are you sure you want to delete asset "${name}" (${tag})?`)) return;
    try {
      await deleteAsset(id);
      toast.success("Asset deleted successfully!");
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Cannot delete asset");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Available":
        return <span className="badge badge-success badge-sm text-white">Available</span>;
      case "Assigned":
      case "In Use":
        return <span className="badge badge-info badge-sm text-white">In Use</span>;
      case "Under Maintenance":
        return <span className="badge badge-warning badge-sm text-white">Under Repair</span>;
      case "Damaged":
        return <span className="badge badge-error badge-sm text-white">Damaged</span>;
      case "Missing":
        return <span className="badge badge-neutral badge-sm">Missing</span>;
      case "Disposed":
      case "Written Off":
        return <span className="badge badge-ghost badge-sm line-through">Disposed</span>;
      default:
        return <span className="badge badge-outline badge-sm">{status}</span>;
    }
  };

  const getConditionBadge = (cond) => {
    switch (cond) {
      case "Excellent":
        return <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded">Excellent</span>;
      case "Good":
        return <span className="text-xs font-semibold text-blue-600 bg-blue-50 dark:bg-blue-950/40 px-2 py-0.5 rounded">Good</span>;
      case "Fair":
        return <span className="text-xs font-semibold text-amber-600 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded">Fair</span>;
      case "Poor":
      case "Damaged":
        return <span className="text-xs font-semibold text-red-600 bg-red-50 dark:bg-red-950/40 px-2 py-0.5 rounded">{cond}</span>;
      default:
        return <span className="text-xs text-gray-500">{cond || "Good"}</span>;
    }
  };

  const printQRLabel = () => {
    const printContent = document.getElementById("printable_qr_card");
    if (!printContent) return;
    const printWin = window.open("", "", "width=400,height=500");
    printWin.document.write(`
      <html>
        <head>
          <title>Asset Tag - ${qrAsset?.asset_id_str}</title>
          <style>
            body { font-family: monospace; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 20px; text-align: center; }
            h2 { margin: 0 0 5px 0; font-size: 16px; }
            p { margin: 2px 0; font-size: 12px; }
            .tag { font-size: 18px; font-weight: bold; margin: 10px 0; letter-spacing: 1px; }
          </style>
        </head>
        <body>
          <h2>${storeSettings?.name || "eHospitality"}</h2>
          <div class="tag">${qrAsset?.asset_id_str}</div>
          ${printContent.innerHTML}
          <p><strong>${qrAsset?.name}</strong></p>
          <p>${qrAsset?.category_name || "General Asset"}</p>
          <script>
            window.onload = function() { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `);
    printWin.document.close();
  };

  return (
    <div className="space-y-4">
      {/* Search & Filter Toolbar */}
      <div className="bg-white dark:bg-neutral-800 p-4 rounded-2xl border border-gray-100 dark:border-neutral-700 shadow-sm flex flex-col md:flex-row gap-3 items-center justify-between">
        <div className="flex flex-1 flex-wrap gap-2 items-center w-full">
          <div className="relative w-full md:w-64">
            <input
              type="text"
              placeholder="Search tag, name, serial..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input input-sm input-bordered w-full pl-8"
            />
            <IconSearch size={16} className="absolute left-2.5 top-2.5 text-gray-400" stroke={iconStroke} />
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="select select-sm select-bordered w-auto"
          >
            <option value="all">All Categories</option>
            {(categories || []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="select select-sm select-bordered w-auto"
          >
            <option value="all">All Statuses</option>
            <option value="Available">Available</option>
            <option value="Assigned">Assigned / In Use</option>
            <option value="Under Maintenance">Under Repair</option>
            <option value="Damaged">Damaged</option>
            <option value="Missing">Missing</option>
            <option value="Disposed">Disposed / Written Off</option>
          </select>

          <select
            value={selectedCondition}
            onChange={(e) => setSelectedCondition(e.target.value)}
            className="select select-sm select-bordered w-auto"
          >
            <option value="all">All Conditions</option>
            <option value="Excellent">Excellent</option>
            <option value="Good">Good</option>
            <option value="Fair">Fair</option>
            <option value="Poor">Poor</option>
            <option value="Damaged">Damaged</option>
          </select>

          {(search || selectedCategory !== "all" || selectedStatus !== "all" || selectedCondition !== "all") && (
            <button
              onClick={() => {
                setSearch("");
                setSelectedCategory("all");
                setSelectedStatus("all");
                setSelectedCondition("all");
              }}
              className="btn btn-sm btn-ghost text-xs text-red-500"
            >
              Reset Filters
            </button>
          )}
        </div>

        <div className="flex gap-2 w-full md:w-auto justify-end">
          <button
            onClick={onOpenAddAsset}
            className="btn btn-sm bg-restro-green hover:bg-emerald-700 text-white border-0 flex items-center gap-1"
          >
            <IconPlus size={16} stroke={iconStroke} /> Register Asset
          </button>
        </div>
      </div>

      {/* Asset Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-gray-100 dark:border-neutral-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-neutral-900/60 text-gray-600 dark:text-gray-300">
                <th>Asset Tag / Code</th>
                <th>Name & Specifications</th>
                <th>Category</th>
                <th>Location & Custody</th>
                <th>Condition</th>
                <th>Status</th>
                <th className="text-right">Capitalized Cost</th>
                <th className="text-right">Net Book Value</th>
                <th className="text-center">QR Tag</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAssets.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-gray-400">
                    <IconBox size={40} className="mx-auto mb-2 opacity-30" stroke={iconStroke} />
                    No assets found matching the selected filters.
                  </td>
                </tr>
              ) : (
                filteredAssets.map((asset) => (
                  <tr key={asset.id} className="hover:bg-emerald-50/30 dark:hover:bg-neutral-700/30">
                    <td>
                      <span className="font-mono font-bold text-emerald-700 dark:text-emerald-400">
                        {asset.asset_id_str}
                      </span>
                      {asset.barcode && <div className="text-[10px] text-gray-400 font-mono">BC: {asset.barcode}</div>}
                    </td>
                    <td>
                      <div className="font-semibold text-gray-800 dark:text-white">{asset.name}</div>
                      <div className="text-xs text-gray-400">
                        {[asset.brand, asset.model, asset.serial_number ? `SN: ${asset.serial_number}` : null]
                          .filter(Boolean)
                          .join(" • ")}
                      </div>
                    </td>
                    <td>
                      <span className="badge badge-ghost badge-sm">{asset.category_name || "Uncategorized"}</span>
                    </td>
                    <td>
                      <div className="text-xs font-medium text-gray-700 dark:text-gray-200">
                        {asset.location || "Default Location"}
                      </div>
                      <div className="text-[11px] text-gray-400">
                        {asset.department ? `${asset.department} • ` : ""}
                        {asset.custodian_name || asset.assigned_to || "No Custodian"}
                      </div>
                    </td>
                    <td>{getConditionBadge(asset.condition)}</td>
                    <td>{getStatusBadge(asset.status)}</td>
                    <td className="text-right font-medium">
                      {currency} {Number(asset.capitalized_cost || asset.purchase_price || 0).toLocaleString()}
                    </td>
                    <td className="text-right font-bold text-purple-600 dark:text-purple-400">
                      {currency} {Number(asset.current_book_value || 0).toLocaleString()}
                    </td>
                    <td className="text-center">
                      <button
                        onClick={() => setQrAsset(asset)}
                        title="View / Print QR Code"
                        className="btn btn-xs btn-ghost text-emerald-600 hover:bg-emerald-50 dark:hover:bg-neutral-700"
                      >
                        <IconQrcode size={18} stroke={iconStroke} />
                      </button>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => onViewAssetDetails(asset.id)}
                          title="Full Details"
                          className="btn btn-xs btn-ghost text-blue-600"
                        >
                          <IconEye size={16} stroke={iconStroke} />
                        </button>
                        <button
                          onClick={() => onOpenTransfer(asset)}
                          title="Transfer / Assign"
                          className="btn btn-xs btn-ghost text-indigo-600"
                        >
                          <IconArrowsExchange size={16} stroke={iconStroke} />
                        </button>
                        <button
                          onClick={() => onOpenDamage(asset)}
                          title="Report Damage"
                          className="btn btn-xs btn-ghost text-amber-600"
                        >
                          <IconAlertTriangle size={16} stroke={iconStroke} />
                        </button>
                        <button
                          onClick={() => onOpenEditAsset(asset)}
                          title="Edit"
                          className="btn btn-xs btn-ghost text-gray-600"
                        >
                          <IconEdit size={16} stroke={iconStroke} />
                        </button>
                        <button
                          onClick={() => handleDelete(asset.id, asset.name, asset.asset_id_str)}
                          title="Delete / Retire"
                          className="btn btn-xs btn-ghost text-red-500 hover:bg-red-50"
                        >
                          <IconTrash size={16} stroke={iconStroke} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="p-3 bg-gray-50 dark:bg-neutral-900/40 border-t border-gray-100 dark:border-neutral-700 text-xs text-gray-500 flex justify-between items-center">
          <span>Showing {filteredAssets.length} of {assets?.length || 0} assets</span>
          <span>Tip: Click QR Code button to print physical sticker labels.</span>
        </div>
      </div>

      {/* QR Code Modal */}
      {qrAsset && (
        <dialog id="modal_qr_code" className="modal modal-open">
          <div className="modal-box max-w-sm text-center">
            <h3 className="font-bold text-lg text-gray-800 dark:text-white">Physical Asset Label</h3>
            <p className="text-xs text-gray-500 mb-4">Scan using any standard smartphone camera or barcode gun</p>

            <div className="bg-white p-4 rounded-2xl border-2 border-dashed border-gray-200 inline-block shadow-inner" id="printable_qr_card">
              <div className="text-xs font-bold text-gray-400 mb-1">{storeSettings?.name || "eHospitality"}</div>
              <div className="flex justify-center p-2">
                <QRCodeSVG
                  value={qrAsset.qr_code || qrAsset.asset_id_str}
                  size={160}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <div className="font-mono font-black text-lg text-emerald-700 tracking-wider mt-1">
                {qrAsset.asset_id_str}
              </div>
              <div className="font-semibold text-sm text-gray-800 mt-0.5">{qrAsset.name}</div>
              <div className="text-xs text-gray-500">{qrAsset.category_name}</div>
              <div className="text-[10px] text-gray-400 mt-1">
                {qrAsset.location ? `Loc: ${qrAsset.location}` : ""}
              </div>
            </div>

            <div className="modal-action justify-between mt-6">
              <button onClick={() => setQrAsset(null)} className="btn btn-sm btn-ghost">
                Close
              </button>
              <button onClick={printQRLabel} className="btn btn-sm bg-restro-green hover:bg-emerald-700 text-white flex items-center gap-1">
                <IconPrinter size={16} stroke={iconStroke} /> Print Label
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
}
